import React from "react";
import { Group, Rect, Shape, Line, Star, RegularPolygon } from "react-konva";
import type { Element, ArrowElement as ArrowEl, CircleElement as CircleEl, LineElement as LineEl, PolygonElement as PolygonEl, RectElement as RectEl, StarElement as StarEl } from "../types";
import { borderDashForStyle, normalizeBorder } from "../utils/border";
import { convertGradientToKonva } from "./utils/gradientConverter";
import { normalizeCurve, normalizeCornerCurve, roundedRectWithCurvePath } from "./utils/shapePaths";
import { FeatheredGroup } from "./CanvasStageFilters";
import { Blur } from "konva/lib/filters/Blur";
import type { KonvaNodeCommonProps } from "./CanvasStageTypes";
import {
  borderAlignDeltaPx,
  colorWithAlpha,
  drawMaskPathOnCtx,
  getSimpleArrowPoints,
  konvaCornerRadiusProp,
  maskClipFunc,
  resolveCornerRadiiPx,
} from "./CanvasStageElementUtils";

export type RenderShapeElementArgs<T extends Element> = {
  el: T;
  common: KonvaNodeCommonProps;
  hoveredElementId: string | null;
  totalViewportScale: number;
  isTransformingRef: React.RefObject<boolean>;
};

export function renderRectElement(args: RenderShapeElementArgs<RectEl>) {
  const { el, common, hoveredElementId, totalViewportScale, isTransformingRef } = args;
  const rect = el as RectEl;
  const hasBlur = (rect.blur || 0) > 0;
  const radiiPx = resolveCornerRadiiPx(rect.cornerRadius, el.width, el.height, rect);
  const cornerRadius = konvaCornerRadiusProp(radiiPx);
  const curve = normalizeCurve(rect);
  const cornerCurve = normalizeCornerCurve(rect);
    const border = normalizeBorder(rect);
    const borderW = border.enabled ? border.width : 0;
    const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
    const borderDash = borderDashForStyle(border.style, borderW);
    const mask = (rect as any)?.mask as any;
    const maskEnabled = !!mask?.enabled;
    const maskFeather = Math.max(0, Number(mask?.feather) || 0);
    const alignDelta = borderAlignDeltaPx(border.align, borderW);
    const borderW2 = Math.max(1, el.width - 2 * alignDelta);
    const borderH2 = Math.max(1, el.height - 2 * alignDelta);
    const borderRadii = radiiPx.map((r) => Math.max(0, r - alignDelta)) as [number, number, number, number];

    const clipCacheKey = maskEnabled
      ? [
          String(mask?.type || "rect"),
          String(mask?.x ?? 0),
          String(mask?.y ?? 0),
          String(mask?.scale ?? 1),
          String(mask?.rotation ?? 0),
          String(mask?.polygonSides ?? ""),
          String(mask?.customPath ?? ""),
          String(curve?.type ?? ""),
          String(curve?.strength ?? ""),
          String(el.width),
          String(el.height),
        ].join("|")
      : "";

    const maskClipProps = maskEnabled
      ? {
          clipFunc: maskClipFunc({ w: el.width, h: el.height, mask, radiiPx, curve }),
        }
      : undefined;

    const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  // Build fill properties (gradient or solid)
  const fillProps = rect.fillGradient
    ? (() => {
        try {
          const gradient: any = rect.fillGradient;
          const stops = Array.isArray(gradient?.stops) ? gradient.stops : [];
          if (stops.length < 2) return { fill: rect.fill ?? "#cccccc" };
          return convertGradientToKonva(
            gradient,
            Math.max(10, rect.width),
            Math.max(10, rect.height),
            rect.rotation || 0
          );
        } catch {
          return { fill: rect.fill ?? "#cccccc" };
        }
      })()
    : { fill: rect.fill };

    const fillNode = curve ? (
      <Shape
        x={0}
        y={0}
        width={el.width}
        height={el.height}
        {...fillProps}
        shadowBlur={rect.shadowBlur || 0}
        shadowOffsetX={rect.shadowOffsetX || 0}
        shadowOffsetY={rect.shadowOffsetY || 0}
        shadowColor={rect.shadowColor || ''}
        shadowOpacity={rect.shadowOpacity !== undefined ? rect.shadowOpacity : 1}
        shadowEnabled={!!(rect.shadowBlur && rect.shadowBlur > 0)}
        filters={hasBlur ? [Blur] : undefined}
        blurRadius={hasBlur ? rect.blur : 0}
        sceneFunc={(ctx, shape) => {
          roundedRectWithCurvePath(ctx as any, el.width, el.height, radiiPx, curve, cornerCurve);
          ctx.fillStrokeShape(shape);
        }}
      />
    ) : cornerCurve.type !== "circular" ? (
      // Use custom Shape for non-circular corner styles (squircle, chamfer)
      <Shape
        x={0}
        y={0}
        width={el.width}
        height={el.height}
        {...fillProps}
        shadowBlur={rect.shadowBlur || 0}
        shadowOffsetX={rect.shadowOffsetX || 0}
        shadowOffsetY={rect.shadowOffsetY || 0}
        shadowColor={rect.shadowColor || ''}
        shadowOpacity={rect.shadowOpacity !== undefined ? rect.shadowOpacity : 1}
        shadowEnabled={!!(rect.shadowBlur && rect.shadowBlur > 0)}
        filters={hasBlur ? [Blur] : undefined}
        blurRadius={hasBlur ? rect.blur : 0}
        sceneFunc={(ctx, shape) => {
          roundedRectWithCurvePath(ctx as any, el.width, el.height, radiiPx, null, cornerCurve);
          ctx.fillStrokeShape(shape);
        }}
      />
    ) : (
      <Rect
        x={0}
        y={0}
        width={el.width}
        height={el.height}
        {...fillProps}
        cornerRadius={cornerRadius}
        shadowBlur={rect.shadowBlur || 0}
        shadowOffsetX={rect.shadowOffsetX || 0}
        shadowOffsetY={rect.shadowOffsetY || 0}
        shadowColor={rect.shadowColor || ''}
        shadowOpacity={rect.shadowOpacity !== undefined ? rect.shadowOpacity : 1}
        shadowEnabled={!!(rect.shadowBlur && rect.shadowBlur > 0)}
        filters={hasBlur ? [Blur] : undefined}
        blurRadius={hasBlur ? rect.blur : 0}
      />
    );

    return (
      <Group key={el.id} {...common} width={el.width} height={el.height}>
        {maskEnabled ? (
          maskFeather > 0.001 ? (
            <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
              {fillNode}
            </FeatheredGroup>
          ) : (
            <Group {...maskClipProps}>{fillNode}</Group>
          )
        ) : (
          fillNode
        )}

        {borderW > 0.001 && borderStroke ? (
          maskEnabled ? (
            <Shape
              x={0}
              y={0}
              width={el.width}
              height={el.height}
              fillEnabled={false}
              stroke={borderStroke as any}
              strokeWidth={borderW}
              dash={borderDash}
              strokeScaleEnabled={false}
              listening
              sceneFunc={(ctx, shape) => {
                drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, radiiPx, curve);
                ctx.fillStrokeShape(shape);
              }}
            />
          ) : curve ? (
            <Shape
              x={alignDelta}
              y={alignDelta}
              width={borderW2}
            height={borderH2}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              roundedRectWithCurvePath(ctx as any, borderW2, borderH2, borderRadii, curve, cornerCurve);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : (
          <Rect
            x={alignDelta}
            y={alignDelta}
            width={borderW2}
            height={borderH2}
            fill="rgba(0,0,0,0)"
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            cornerRadius={konvaCornerRadiusProp(borderRadii) as any}
            strokeScaleEnabled={false}
              listening
            />
          )
        ) : null}

        {showMaskOutline ? (
          <Shape
            x={0}
            y={0}
            width={el.width}
            height={el.height}
            fillEnabled={false}
            stroke="rgba(59,130,246,0.9)"
            strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
            dash={[6, 4]}
            strokeScaleEnabled={false}
            listening={false}
            perfectDrawEnabled={false}
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, radiiPx, curve);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : null}
      </Group>
    );
}

export function renderCircleElement(args: RenderShapeElementArgs<CircleEl>) {
  const { el, common, hoveredElementId, totalViewportScale, isTransformingRef } = args;
  const c = el as CircleEl;
  const rr = Math.max(1, Math.floor(Math.min(el.width, el.height) / 2));
  const hasBlur = (c.blur || 0) > 0;
  const border = normalizeBorder(c);
  const borderW = border.enabled ? border.width : 0;
    const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
    const borderDash = borderDashForStyle(border.style, borderW);
    const alignDelta = borderAlignDeltaPx(border.align, borderW);
    const borderW2 = Math.max(1, el.width - 2 * alignDelta);
    const borderH2 = Math.max(1, el.height - 2 * alignDelta);
    const borderRr = Math.max(1, Math.floor(Math.min(borderW2, borderH2) / 2));
    const mask = (c as any)?.mask as any;
    const maskEnabled = !!mask?.enabled;
    const maskFeather = Math.max(0, Number(mask?.feather) || 0);
    const clipCacheKey = maskEnabled
      ? [
          String(mask?.type || "rect"),
          String(mask?.x ?? 0),
          String(mask?.y ?? 0),
          String(mask?.scale ?? 1),
          String(mask?.rotation ?? 0),
          String(mask?.polygonSides ?? ""),
          String(mask?.customPath ?? ""),
          String(el.width),
          String(el.height),
        ].join("|")
      : "";
    const maskClipProps = maskEnabled
      ? {
          clipFunc: maskClipFunc({ w: el.width, h: el.height, mask, radiiPx: [0, 0, 0, 0], curve: null }),
        }
      : undefined;
    const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  // Build fill properties (gradient or solid)
  const fillProps = c.fillGradient
    ? (() => {
        try {
          const gradient: any = c.fillGradient;
          const stops = Array.isArray(gradient?.stops) ? gradient.stops : [];
          if (stops.length < 2) return { fill: c.fill ?? "#cccccc" };
          return convertGradientToKonva(
            gradient,
            Math.max(10, c.width),
            Math.max(10, c.height),
            c.rotation || 0
          );
        } catch {
          return { fill: c.fill ?? "#cccccc" };
        }
      })()
    : { fill: c.fill };

    return (
      <Group key={el.id} {...common} width={el.width} height={el.height}>
        {maskEnabled ? (
          maskFeather > 0.001 ? (
            <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
              <Rect
                x={0}
                y={0}
                width={el.width}
                height={el.height}
                {...fillProps}
                cornerRadius={rr}
                shadowBlur={c.shadowBlur || 0}
                shadowOffsetX={c.shadowOffsetX || 0}
                shadowOffsetY={c.shadowOffsetY || 0}
                shadowColor={c.shadowColor || ''}
                shadowOpacity={c.shadowOpacity !== undefined ? c.shadowOpacity : 1}
                shadowEnabled={!!(c.shadowBlur && c.shadowBlur > 0)}
                filters={hasBlur ? [Blur] : undefined}
                blurRadius={hasBlur ? c.blur : 0}
              />
            </FeatheredGroup>
          ) : (
            <Group {...maskClipProps}>
              <Rect
                x={0}
                y={0}
                width={el.width}
                height={el.height}
                {...fillProps}
                cornerRadius={rr}
                shadowBlur={c.shadowBlur || 0}
                shadowOffsetX={c.shadowOffsetX || 0}
                shadowOffsetY={c.shadowOffsetY || 0}
                shadowColor={c.shadowColor || ''}
                shadowOpacity={c.shadowOpacity !== undefined ? c.shadowOpacity : 1}
                shadowEnabled={!!(c.shadowBlur && c.shadowBlur > 0)}
                filters={hasBlur ? [Blur] : undefined}
                blurRadius={hasBlur ? c.blur : 0}
              />
            </Group>
          )
        ) : (
          <Rect
            x={0}
            y={0}
            width={el.width}
            height={el.height}
            {...fillProps}
            cornerRadius={rr}
            shadowBlur={c.shadowBlur || 0}
            shadowOffsetX={c.shadowOffsetX || 0}
            shadowOffsetY={c.shadowOffsetY || 0}
            shadowColor={c.shadowColor || ''}
            shadowOpacity={c.shadowOpacity !== undefined ? c.shadowOpacity : 1}
            shadowEnabled={!!(c.shadowBlur && c.shadowBlur > 0)}
            filters={hasBlur ? [Blur] : undefined}
            blurRadius={hasBlur ? c.blur : 0}
          />
        )}

        {borderW > 0.001 && borderStroke ? (
          maskEnabled ? (
            <Shape
              x={0}
              y={0}
              width={el.width}
              height={el.height}
              fillEnabled={false}
              stroke={borderStroke as any}
              strokeWidth={borderW}
              dash={borderDash}
              strokeScaleEnabled={false}
              listening
              sceneFunc={(ctx, shape) => {
                drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
                ctx.fillStrokeShape(shape);
              }}
            />
          ) : (
            <Rect
              x={alignDelta}
              y={alignDelta}
              width={borderW2}
              height={borderH2}
              fill="rgba(0,0,0,0)"
              stroke={borderStroke as any}
              strokeWidth={borderW}
              dash={borderDash}
              cornerRadius={borderRr}
              strokeScaleEnabled={false}
              listening
            />
          )
        ) : null}

        {showMaskOutline ? (
          <Shape
            x={0}
            y={0}
            width={el.width}
            height={el.height}
            fillEnabled={false}
            stroke="rgba(59,130,246,0.9)"
            strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
            dash={[6, 4]}
            strokeScaleEnabled={false}
            listening={false}
            perfectDrawEnabled={false}
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : null}
      </Group>
    );
}

export function renderLineElement(args: RenderShapeElementArgs<LineEl>) {
  const { el, common, hoveredElementId, totalViewportScale, isTransformingRef } = args;
  const lineEl = el as LineEl;
  const hasBlur = (lineEl.blur || 0) > 0;
  const border = normalizeBorder(lineEl);
  const borderW = border.enabled ? border.width : 0;
  const stroke = border.color ? colorWithAlpha(border.color, border.opacity) : (lineEl.stroke || '#000000');
  const dash = borderDashForStyle(border.style, borderW) ?? lineEl.dashPattern;
  const mask = (lineEl as any)?.mask as any;
  const maskEnabled = !!mask?.enabled;
  const showMaskOutline = maskEnabled && hoveredElementId === el.id;
    const maskClipProps = maskEnabled
      ? {
          clipFunc: maskClipFunc({ w: el.width, h: el.height, mask, radiiPx: [0, 0, 0, 0], curve: null }),
        }
      : undefined;

  const lineNode = (
    <Line
      points={lineEl.points || [0, 0, el.width, el.height]}
      stroke={stroke}
      strokeWidth={borderW || 0}
      dash={dash}
      lineCap="round"
      lineJoin="round"
      strokeScaleEnabled={false}
      shadowBlur={lineEl.shadowBlur || 0}
      shadowOffsetX={lineEl.shadowOffsetX || 0}
      shadowOffsetY={lineEl.shadowOffsetY || 0}
      shadowColor={lineEl.shadowColor || ''}
      shadowOpacity={lineEl.shadowOpacity !== undefined ? lineEl.shadowOpacity : 1}
      shadowEnabled={!!(lineEl.shadowBlur && lineEl.shadowBlur > 0)}
      filters={hasBlur ? [Blur] : undefined}
      blurRadius={hasBlur ? lineEl.blur : 0}
    />
  );

  if (maskEnabled) {
    return (
      <Group key={el.id} {...common} width={el.width} height={el.height}>
        <Group {...maskClipProps}>{lineNode}</Group>
        {showMaskOutline ? (
          <Shape
            x={0}
            y={0}
            width={el.width}
            height={el.height}
            fillEnabled={false}
            stroke="rgba(59,130,246,0.9)"
            strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
            dash={[6, 4]}
            strokeScaleEnabled={false}
            listening={false}
            perfectDrawEnabled={false}
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : null}
      </Group>
    );
  }
  return (
    <Line
      key={el.id}
      {...common}
      {...(lineNode.props as any)}
    />
  );
}

export function renderStarElement(args: RenderShapeElementArgs<StarEl>) {
  const { el, common, hoveredElementId, totalViewportScale, isTransformingRef } = args;
  const starEl = el as StarEl;
  const numPoints = starEl.numPoints || 5;
  const outerRadius = Math.min(el.width, el.height) / 2;
  const innerRadius = outerRadius * (starEl.innerRadius || 0.5);
  const hasBlur = (starEl.blur || 0) > 0;
  const border = normalizeBorder(starEl);
  const borderW = border.enabled ? border.width : 0;
  const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
  const dash = borderDashForStyle(border.style, borderW);
  const alignDelta = borderAlignDeltaPx(border.align, borderW);
  const borderOuterRadius = Math.max(1, outerRadius - alignDelta);
  const borderInnerRadius = Math.max(1, borderOuterRadius * (starEl.innerRadius || 0.5));
  const mask = (starEl as any)?.mask as any;
  const maskEnabled = !!mask?.enabled;
  const maskFeather = Math.max(0, Number(mask?.feather) || 0);
  const clipCacheKey = maskEnabled
    ? [
        String(mask?.type || "rect"),
        String(mask?.x ?? 0),
        String(mask?.y ?? 0),
        String(mask?.scale ?? 1),
        String(mask?.rotation ?? 0),
        String(mask?.polygonSides ?? ""),
        String(mask?.customPath ?? ""),
        String(el.width),
        String(el.height),
      ].join("|")
    : "";
    const maskClipProps = maskEnabled
      ? {
          clipFunc: maskClipFunc({ w: el.width, h: el.height, mask, radiiPx: [0, 0, 0, 0], curve: null }),
        }
      : undefined;
  const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  // Build fill properties (gradient or solid)
  const fillProps = starEl.fillGradient
    ? (() => {
        try {
          const gradient: any = starEl.fillGradient;
          const stops = Array.isArray(gradient?.stops) ? gradient.stops : [];
          if (stops.length < 2) return { fill: starEl.fill ?? "#FFD700" };
          return convertGradientToKonva(
            gradient,
            Math.max(10, el.width),
            Math.max(10, el.height),
            el.rotation || 0
          );
        } catch {
          return { fill: starEl.fill ?? "#FFD700" };
        }
      })()
    : { fill: starEl.fill || '#FFD700' };

  return (
    <Group key={el.id} {...common} width={el.width} height={el.height}>
      {maskEnabled ? (
        maskFeather > 0.001 ? (
          <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
            <Star
              x={0}
              y={0}
              numPoints={numPoints}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              {...fillProps}
              offsetX={-el.width / 2}
              offsetY={-el.height / 2}
              shadowBlur={starEl.shadowBlur || 0}
              shadowOffsetX={starEl.shadowOffsetX || 0}
              shadowOffsetY={starEl.shadowOffsetY || 0}
              shadowColor={starEl.shadowColor || ''}
              shadowOpacity={starEl.shadowOpacity !== undefined ? starEl.shadowOpacity : 1}
              shadowEnabled={!!(starEl.shadowBlur && starEl.shadowBlur > 0)}
              filters={hasBlur ? [Blur] : undefined}
              blurRadius={hasBlur ? starEl.blur : 0}
            />
          </FeatheredGroup>
        ) : (
          <Group {...maskClipProps}>
            <Star
              x={0}
              y={0}
              numPoints={numPoints}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              {...fillProps}
              offsetX={-el.width / 2}
              offsetY={-el.height / 2}
              shadowBlur={starEl.shadowBlur || 0}
              shadowOffsetX={starEl.shadowOffsetX || 0}
              shadowOffsetY={starEl.shadowOffsetY || 0}
              shadowColor={starEl.shadowColor || ''}
              shadowOpacity={starEl.shadowOpacity !== undefined ? starEl.shadowOpacity : 1}
              shadowEnabled={!!(starEl.shadowBlur && starEl.shadowBlur > 0)}
              filters={hasBlur ? [Blur] : undefined}
              blurRadius={hasBlur ? starEl.blur : 0}
            />
          </Group>
        )
      ) : (
        <Star
          x={0}
          y={0}
          numPoints={numPoints}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          {...fillProps}
          offsetX={-el.width / 2}
          offsetY={-el.height / 2}
          shadowBlur={starEl.shadowBlur || 0}
          shadowOffsetX={starEl.shadowOffsetX || 0}
          shadowOffsetY={starEl.shadowOffsetY || 0}
          shadowColor={starEl.shadowColor || ''}
          shadowOpacity={starEl.shadowOpacity !== undefined ? starEl.shadowOpacity : 1}
          shadowEnabled={!!(starEl.shadowBlur && starEl.shadowBlur > 0)}
          filters={hasBlur ? [Blur] : undefined}
          blurRadius={hasBlur ? starEl.blur : 0}
        />
      )}
      {borderW > 0.001 && borderStroke ? (
        maskEnabled ? (
          <Shape
            x={0}
            y={0}
            width={el.width}
            height={el.height}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={dash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : (
          <Star
            x={0}
            y={0}
            numPoints={numPoints}
            innerRadius={borderInnerRadius}
            outerRadius={borderOuterRadius}
            fill="rgba(0,0,0,0)"
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={dash}
            strokeScaleEnabled={false}
            offsetX={-el.width / 2}
            offsetY={-el.height / 2}
          />
        )
      ) : null}

      {showMaskOutline ? (
        <Shape
          x={0}
          y={0}
          width={el.width}
          height={el.height}
          fillEnabled={false}
          stroke="rgba(59,130,246,0.9)"
          strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
          dash={[6, 4]}
          strokeScaleEnabled={false}
          listening={false}
          perfectDrawEnabled={false}
          sceneFunc={(ctx, shape) => {
            drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
            ctx.fillStrokeShape(shape);
          }}
        />
      ) : null}
    </Group>
  );
}

export function renderPolygonElement(args: RenderShapeElementArgs<PolygonEl>) {
  const { el, common, hoveredElementId, totalViewportScale, isTransformingRef } = args;
  const polyEl = el as PolygonEl;
  const sides = polyEl.sides || 6;
  const radius = Math.min(el.width, el.height) / 2;
  const hasBlur = (polyEl.blur || 0) > 0;
  const border = normalizeBorder(polyEl);
  const borderW = border.enabled ? border.width : 0;
  const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
  const dash = borderDashForStyle(border.style, borderW);
  const alignDelta = borderAlignDeltaPx(border.align, borderW);
  const borderRadius = Math.max(1, radius - alignDelta);
  const mask = (polyEl as any)?.mask as any;
  const maskEnabled = !!mask?.enabled;
  const maskFeather = Math.max(0, Number(mask?.feather) || 0);
  const clipCacheKey = maskEnabled
    ? [
        String(mask?.type || "rect"),
        String(mask?.x ?? 0),
        String(mask?.y ?? 0),
        String(mask?.scale ?? 1),
        String(mask?.rotation ?? 0),
        String(mask?.polygonSides ?? ""),
        String(mask?.customPath ?? ""),
        String(el.width),
        String(el.height),
      ].join("|")
    : "";
    const maskClipProps = maskEnabled
      ? {
          clipFunc: maskClipFunc({ w: el.width, h: el.height, mask, radiiPx: [0, 0, 0, 0], curve: null }),
        }
      : undefined;
  const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  // Build fill properties (gradient or solid)
  const fillProps = polyEl.fillGradient
    ? (() => {
        try {
          const gradient: any = polyEl.fillGradient;
          const stops = Array.isArray(gradient?.stops) ? gradient.stops : [];
          if (stops.length < 2) return { fill: polyEl.fill ?? "#3B82F6" };
          return convertGradientToKonva(
            gradient,
            Math.max(10, el.width),
            Math.max(10, el.height),
            el.rotation || 0
          );
        } catch {
          return { fill: polyEl.fill ?? "#3B82F6" };
        }
      })()
    : { fill: polyEl.fill || '#3B82F6' };

  return (
    <Group key={el.id} {...common} width={el.width} height={el.height}>
      {maskEnabled ? (
        maskFeather > 0.001 ? (
          <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
            <RegularPolygon
              x={0}
              y={0}
              sides={sides}
              radius={radius}
              {...fillProps}
              offsetX={-el.width / 2}
              offsetY={-el.height / 2}
              shadowBlur={polyEl.shadowBlur || 0}
              shadowOffsetX={polyEl.shadowOffsetX || 0}
              shadowOffsetY={polyEl.shadowOffsetY || 0}
              shadowColor={polyEl.shadowColor || ''}
              shadowOpacity={polyEl.shadowOpacity !== undefined ? polyEl.shadowOpacity : 1}
              shadowEnabled={!!(polyEl.shadowBlur && polyEl.shadowBlur > 0)}
              filters={hasBlur ? [Blur] : undefined}
              blurRadius={hasBlur ? polyEl.blur : 0}
            />
          </FeatheredGroup>
        ) : (
          <Group {...maskClipProps}>
            <RegularPolygon
              x={0}
              y={0}
              sides={sides}
              radius={radius}
              {...fillProps}
              offsetX={-el.width / 2}
              offsetY={-el.height / 2}
              shadowBlur={polyEl.shadowBlur || 0}
              shadowOffsetX={polyEl.shadowOffsetX || 0}
              shadowOffsetY={polyEl.shadowOffsetY || 0}
              shadowColor={polyEl.shadowColor || ''}
              shadowOpacity={polyEl.shadowOpacity !== undefined ? polyEl.shadowOpacity : 1}
              shadowEnabled={!!(polyEl.shadowBlur && polyEl.shadowBlur > 0)}
              filters={hasBlur ? [Blur] : undefined}
              blurRadius={hasBlur ? polyEl.blur : 0}
            />
          </Group>
        )
      ) : (
        <RegularPolygon
          x={0}
          y={0}
          sides={sides}
          radius={radius}
          {...fillProps}
          offsetX={-el.width / 2}
          offsetY={-el.height / 2}
          shadowBlur={polyEl.shadowBlur || 0}
          shadowOffsetX={polyEl.shadowOffsetX || 0}
          shadowOffsetY={polyEl.shadowOffsetY || 0}
          shadowColor={polyEl.shadowColor || ''}
          shadowOpacity={polyEl.shadowOpacity !== undefined ? polyEl.shadowOpacity : 1}
          shadowEnabled={!!(polyEl.shadowBlur && polyEl.shadowBlur > 0)}
          filters={hasBlur ? [Blur] : undefined}
          blurRadius={hasBlur ? polyEl.blur : 0}
        />
      )}
      {borderW > 0.001 && borderStroke ? (
        maskEnabled ? (
          <Shape
            x={0}
            y={0}
            width={el.width}
            height={el.height}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={dash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : (
          <RegularPolygon
            x={0}
            y={0}
            sides={sides}
            radius={borderRadius}
            fill="rgba(0,0,0,0)"
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={dash}
            strokeScaleEnabled={false}
            offsetX={-el.width / 2}
            offsetY={-el.height / 2}
          />
        )
      ) : null}

      {showMaskOutline ? (
        <Shape
          x={0}
          y={0}
          width={el.width}
          height={el.height}
          fillEnabled={false}
          stroke="rgba(59,130,246,0.9)"
          strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
          dash={[6, 4]}
          strokeScaleEnabled={false}
          listening={false}
          perfectDrawEnabled={false}
          sceneFunc={(ctx, shape) => {
            drawMaskPathOnCtx(ctx as any, el.width, el.height, mask, [0, 0, 0, 0], null);
            ctx.fillStrokeShape(shape);
          }}
        />
      ) : null}
    </Group>
  );
}

export function renderArrowElement(args: RenderShapeElementArgs<ArrowEl>) {
  const { el, common, hoveredElementId, totalViewportScale, isTransformingRef } = args;
  const arrowEl = el as ArrowEl;
  const direction = arrowEl.direction || 'right';
  const style = arrowEl.style || 'simple';
  const hasBlur = (arrowEl.blur || 0) > 0;
  const border = normalizeBorder(arrowEl);
  const borderW = border.enabled ? border.width : 0;
  const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
    const dash = borderDashForStyle(border.style, borderW);
    const alignDelta = borderAlignDeltaPx(border.align, borderW);
    const fill = arrowEl.fill;
    const mask = (arrowEl as any)?.mask as any;
    const maskEnabled = !!mask?.enabled;
    const maskFeather = Math.max(0, Number(mask?.feather) || 0);
    const clipCacheKey = maskEnabled
      ? [
          String(mask?.type || "rect"),
          String(mask?.x ?? 0),
          String(mask?.y ?? 0),
          String(mask?.scale ?? 1),
          String(mask?.rotation ?? 0),
          String(mask?.polygonSides ?? ""),
          String(mask?.customPath ?? ""),
          String(el.width),
          String(el.height),
        ].join("|")
      : "";
    const maskClipProps = maskEnabled
      ? {
          clipFunc: maskClipFunc({ w: el.width, h: el.height, mask, radiiPx: [0, 0, 0, 0], curve: null }),
        }
      : undefined;
    const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  // Calculate arrow points based on direction
  let points: number[] = [];
  const w = el.width;
  const h = el.height;
  const arrowHeadSize = Math.min(w, h) * 0.3;

  // Common shadow/blur props
  const effectProps = {
    shadowBlur: arrowEl.shadowBlur || 0,
    shadowOffsetX: arrowEl.shadowOffsetX || 0,
    shadowOffsetY: arrowEl.shadowOffsetY || 0,
    shadowColor: arrowEl.shadowColor || '',
    shadowOpacity: arrowEl.shadowOpacity !== undefined ? arrowEl.shadowOpacity : 1,
    shadowEnabled: !!(arrowEl.shadowBlur && arrowEl.shadowBlur > 0),
    filters: hasBlur ? [Blur] : undefined,
    blurRadius: hasBlur ? arrowEl.blur : 0,
  };

  if (style === 'simple') {
    // Simple line arrow
    points = getSimpleArrowPoints(direction as "right" | "left" | "up" | "down", w, h);

      const arrowNode = (
        <Line
          points={points}
          stroke={borderStroke || fill || '#000000'}
          strokeWidth={borderW || 0}
          dash={dash}
          lineCap="round"
          pointerLength={arrowHeadSize}
          pointerWidth={arrowHeadSize}
          pointerAtBeginning={false}
          pointerAtEnding={true}
          strokeScaleEnabled={false}
          {...effectProps}
        />
      );

      if (maskEnabled) {
        return (
          <Group key={el.id} {...common} width={w} height={h}>
            {maskFeather > 0.001 ? (
              <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
                {arrowNode}
              </FeatheredGroup>
            ) : (
              <Group {...maskClipProps}>{arrowNode}</Group>
            )}
            {showMaskOutline ? (
              <Shape
                x={0}
                y={0}
                width={w}
                height={h}
                fillEnabled={false}
                stroke="rgba(59,130,246,0.9)"
                strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
                dash={[6, 4]}
                strokeScaleEnabled={false}
                listening={false}
                perfectDrawEnabled={false}
                sceneFunc={(ctx, shape) => {
                  drawMaskPathOnCtx(ctx as any, w, h, mask, [0, 0, 0, 0], null);
                  ctx.fillStrokeShape(shape);
                }}
              />
            ) : null}
          </Group>
        );
      }

      return (
        <Line key={el.id} {...common} {...(arrowNode.props as any)} />
      );
    } else {
    // Block arrow (filled polygon)
    switch (direction) {
      case 'right':
        points = [
          0, h*0.3,
          w*0.6, h*0.3,
          w*0.6, 0,
          w, h/2,
          w*0.6, h,
          w*0.6, h*0.7,
          0, h*0.7
        ];
        break;
      case 'left':
        points = [
          w, h*0.3,
          w*0.4, h*0.3,
          w*0.4, 0,
          0, h/2,
          w*0.4, h,
          w*0.4, h*0.7,
          w, h*0.7
        ];
        break;
      case 'up':
        points = [
          w*0.3, h,
          w*0.3, h*0.4,
          0, h*0.4,
          w/2, 0,
          w, h*0.4,
          w*0.7, h*0.4,
          w*0.7, h
        ];
        break;
      case 'down':
        points = [
          w*0.3, 0,
          w*0.3, h*0.6,
          0, h*0.6,
          w/2, h,
          w, h*0.6,
          w*0.7, h*0.6,
          w*0.7, 0
        ];
        break;
    }

    const scalePoints = (pts: number[], sx: number, sy: number, cx: number, cy: number) => {
      const out: number[] = [];
      for (let i = 0; i < pts.length; i += 2) {
        const px = pts[i] ?? 0;
        const py = pts[i + 1] ?? 0;
        out.push(cx + (px - cx) * sx, cy + (py - cy) * sy);
      }
      return out;
    };

    const sx = w > 0.001 ? Math.max(0.01, (w - 2 * alignDelta) / w) : 1;
    const sy = h > 0.001 ? Math.max(0.01, (h - 2 * alignDelta) / h) : 1;
    const borderPoints = borderW > 0.001 ? scalePoints(points, sx, sy, w / 2, h / 2) : points;

      const fillNode = <Line x={0} y={0} points={points} fill={fill || '#000000'} strokeWidth={0} closed {...effectProps} />;

      return (
        <Group key={el.id} {...common} width={w} height={h}>
          {maskEnabled ? (
            maskFeather > 0.001 ? (
              <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
                {fillNode}
              </FeatheredGroup>
            ) : (
              <Group {...maskClipProps}>{fillNode}</Group>
            )
          ) : (
            fillNode
          )}

          {borderW > 0.001 && borderStroke ? (
            maskEnabled ? (
              <Shape
                x={0}
                y={0}
                width={w}
                height={h}
                fillEnabled={false}
                stroke={borderStroke as any}
                strokeWidth={borderW}
                dash={dash}
                strokeScaleEnabled={false}
                listening
                sceneFunc={(ctx, shape) => {
                  drawMaskPathOnCtx(ctx as any, w, h, mask, [0, 0, 0, 0], null);
                  ctx.fillStrokeShape(shape);
                }}
              />
            ) : (
              <Line
                x={0}
                y={0}
                points={borderPoints}
                fill="rgba(0,0,0,0)"
                stroke={borderStroke as any}
                strokeWidth={borderW}
                dash={dash}
                closed
                strokeScaleEnabled={false}
              />
            )
          ) : null}

          {showMaskOutline ? (
            <Shape
              x={0}
              y={0}
              width={w}
              height={h}
              fillEnabled={false}
              stroke="rgba(59,130,246,0.9)"
              strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
              dash={[6, 4]}
              strokeScaleEnabled={false}
              listening={false}
              perfectDrawEnabled={false}
              sceneFunc={(ctx, shape) => {
                drawMaskPathOnCtx(ctx as any, w, h, mask, [0, 0, 0, 0], null);
                ctx.fillStrokeShape(shape);
              }}
            />
          ) : null}
        </Group>
      );
    }
}
